/**
 * trial-link — the card-click / deep-link contract for the single-trial drawer
 * (Story I), decoupled from Story D's board so the two can land independently.
 *
 * The selected trial lives in the URL hash as `#trial=<id>`. That one location is
 * both the deep link (load the page on that hash and the drawer opens) and the
 * click target (a board card is an anchor to `trialHref(id)`, or calls
 * `selectTrial(id)`), so "opens on card click" and "opens on a card deep link"
 * are the same mechanism — no shared in-memory state to thread through Story D.
 *
 * Keeping selection in the URL also makes a single trial shareable (Principle 3)
 * and survives a reload via the browser's own history.
 */

const TRIAL_HASH_PREFIX = '#trial='

/** The href a board card points at to open the drawer on `trialId` (deep-linkable). */
export function trialHref(trialId: string): string {
  return `${TRIAL_HASH_PREFIX}${encodeURIComponent(trialId)}`
}

/** Read the selected trial id from a location hash, or null when none is selected. */
export function readTrialFromHash(hash: string): string | null {
  if (!hash.startsWith(TRIAL_HASH_PREFIX)) {
    return null
  }
  const raw = hash.slice(TRIAL_HASH_PREFIX.length)
  if (raw.length === 0) {
    return null
  }
  try {
    return decodeURIComponent(raw)
  } catch {
    // A malformed escape sequence is not a valid selection — treat as none.
    return null
  }
}

/** Read the selected trial id from the live `location`, or null. */
export function readTrialFromLocation(loc: { hash: string } | undefined = globalThis.location): string | null {
  return loc ? readTrialFromHash(loc.hash) : null
}

/** Open the drawer on `trialId` by writing the hash (what a card click does). */
export function selectTrial(trialId: string): void {
  if (globalThis.location) {
    globalThis.location.hash = trialHref(trialId)
  }
}

/** Close the drawer by clearing the trial hash without adding a history entry. */
export function clearTrialSelection(): void {
  const loc = globalThis.location
  const history = globalThis.history
  if (!loc) {
    return
  }
  if (history?.replaceState) {
    history.replaceState(null, '', `${loc.pathname}${loc.search}`)
  } else {
    loc.hash = ''
  }
}
